import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AgGridReact } from "ag-grid-react";

import {
  Box,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Drawer,
  Divider,
  Stack,
  Paper,
  TextField,
  InputAdornment,
  CircularProgress,
  Grid
} from "@mui/material";

import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

import { getScheduledJobsEndpoint, getScheduledJobDetailEndpoint } from "../../../common/endpoints";
import { getScheduledJobsSlice } from "../../../redux/actions/getScheduledJobsSlice";
import { getScheduledJobDetailSlice } from "../../../redux/actions/getScheduledJobDetailSlice";
import ButtonComponent from "../../../components/common/button/Button";
import Modal from "../../../components/common/modal/Modal";

/** Map API requestStatus values to display labels */
const normalizeStatus = (status, uiMessage) => {
  switch ((status || "").toUpperCase()) {
    case "SAP_SUBMITTED": return uiMessage || "-";
    case "COMPLETED": return uiMessage || "-";
    case "FAILED": return "Failed";
    case "IN_PROGRESS": return "In Progress";
    case "PENDING": return "Pending";
    default: return status || "—";
  }
};

/** Derive initials from a full name string */
const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0].toUpperCase())
    .slice(0, 2)
    .join("");

/** Format "2026-07-02" + "13:31:19" → "02-Jul-2026 01:31 PM" */
const formatDateTime = (date, time) => {
  if (!date) return "—";
  try {
    const dt = new Date(`${date}T${time || "00:00:00"}`);
    const d = dt.getDate().toString().padStart(2, "0");
    const mon = dt.toLocaleString("en-US", { month: "short" });
    const yr = dt.getFullYear();
    const h = dt.getHours();
    const m = dt.getMinutes().toString().padStart(2, "0");
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = ((h % 12) || 12).toString().padStart(2, "0");
    return `${d}-${mon}-${yr} ${h12}:${m} ${ampm}`;
  } catch {
    return `${date} ${time || ""}`.trim();
  }
};

const StatusChip = ({ status }) => {
  const config = {
   
    Failed: {
      bg: "#fee2e2",
      color: "#ef4444",
    },
    "In Progress": {
      bg: "#ffedd5",
      color: "#f97316",
    },
    Pending: {
      bg: "#e0f2fe",
      color: "#0369a1",
    },
  };

  return (
    <Chip
      label={`${status}`}
      size="small"
      sx={{
        backgroundColor: config[status]?.bg || "#d8f3dc",
        color: config[status]?.color|| "#16a34a",
        fontWeight: 600,
        minWidth: 120,
      }}
    />
  );
};

export default function ScheduledJobs() {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const [gridApi, setGridApi] = useState();
  const [rowData, setRowData] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [jobDetail, setJobDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [openJobDetailsModal, setOpenJobDetailsModal] = useState(false);
  const selectedRegion = useSelector((state) => state.setRegionPlantStore.selectedRegion);
  const plantNameOnly = useSelector((state) => state.setRegionPlantStore.selectedPlant);
  const selectedPlant = plantNameOnly
    ? plantNameOnly.split(" - ")[0]
    : null;
  const userRole = localStorage.getItem('userRole');
  const userEmail = localStorage.getItem("userEmail");


  const handleShowJobDetailsModal = () => {
    setOpenJobDetailsModal(true);
  }

  const handleCloseJobDetailsModal = () => {
    setOpenJobDetailsModal(false)
  }

  const fetchScheduledJobs = useCallback(async () => {
    setLoadingJobs(true);
    try {
      const response = await dispatch(
        getScheduledJobsSlice({
          dynamicEndpoint: getScheduledJobsEndpoint,
          skipUserEmailParam: true,
          params: {
            userEmail: userEmail,
            plant: selectedPlant || "ALL",
            region: ['Global Planner', 'Global Viewer', 'App Admin'].includes(userRole) ? "ALL" : (selectedRegion || "ALL")
          }
        })
      );
      if (response?.meta?.requestStatus === "fulfilled") {
        const jobs = Array.isArray(response.payload) ? response.payload : [];
        const mapped = jobs.map((job) => ({
          requestId: job.requestId,
          description: job.description,
          requestDate: job.createdDateTime,
          requestedBy: job.userName || "—",
          initials: getInitials(job.userName),
          status: normalizeStatus(job.requestStatus, job.uiMessage),
          uiMessage: job.uiMessage || ""
        }));
        setRowData(mapped);
      }
    } catch (error) {
      console.error("Failed to fetch scheduled jobs:", error);
    } finally {
      setLoadingJobs(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchScheduledJobs();
  }, [fetchScheduledJobs]);

  useEffect(() => {
    if (gridApi) {
      gridApi.setGridOption("quickFilterText", searchText);
    }
  }, [gridApi, searchText]);

  const handleView = useCallback(async (row) => {
    setSelectedRow(row);
    setJobDetail(null);
    // setDrawerOpen(true);
    setOpenJobDetailsModal(true);
    setLoadingDetail(true);
    try {
      const response = await dispatch(
        getScheduledJobDetailSlice({
          dynamicEndpoint: `${getScheduledJobDetailEndpoint}/${row.requestId}`,
          skipUserEmailParam: true,
        })
      );
      if (response?.meta?.requestStatus === "fulfilled") {
        setJobDetail(response.payload);
      }
    } catch (error) {
      console.error("Failed to fetch job detail:", error);
    } finally {
      setLoadingDetail(false);
    }
  }, [dispatch]);

  const onGridReady = (params) => {
    setGridApi(params.api);
    gridRef.current = params.api;
  };

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agMultiColumnFilter",
      resizable: true,
    }),
    []
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Request ID",
        field: "requestId",
        flex: 1,
        cellStyle: {
          color: "#1565c0",
          fontWeight: 700,
        },
      },
      {
        headerName: "Request Date & Time (UTC)",
        field: "requestDate",
        flex: 1.8,
        // cellRenderer: (params) => (
        //   <Typography sx={{ fontSize: 14 }}>
        //     {params.value} UTC
        //   </Typography>
        // )
      },
      {
        headerName: "Requested By",
        field: "requestedBy",
        flex: 1.5,
        // cellRenderer: (params) => (
        //   <Stack direction="row" spacing={1.5} alignItems="center">
        //     <Avatar
        //       sx={{
        //         width: 34,
        //         height: 34,
        //         fontSize: 12,
        //         bgcolor: "#e3f2fd",
        //         color: "#1565c0",
        //         fontWeight: 700,
        //       }}
        //     >
        //       {params.data.initials}
        //     </Avatar>

        //     <Typography sx={{ fontSize: 14 }}>
        //       {params.value}
        //     </Typography>
        //   </Stack>
        // ),
      },
      {
        headerName: "Description",
        field: "description",
        flex: 1.5,
      },
      {
        headerName: "Status",
        field: "status",
        flex: 1.8,
        cellRenderer: (params) => (
          <StatusChip status={params.value} />
        ),
      },
      {
        headerName: "View",
        flex: 0.8,
        sortable: false,
        filter: false,
        cellRenderer: (params) => (
          <IconButton
            onClick={() => handleView(params.data)}
            color="default"
          >
            <RemoveRedEyeOutlinedIcon />
          </IconButton>
        ),
      },
    ],
    [handleView]
  );

  const ShowJobDetailsFooter = ({ onClickCancel }) => {
    return (
      <div className='finalLTupdatedfooter-btn-wrapper'>
        <ButtonComponent
          label="Close"
          type="primary"
          maxWidth="80px"
          onClick={onClickCancel}
        />
      </div>
    );
  };
  const ShowJobDetailsContent = ({ jobDetail }) => {
    return (
      <div>
        <Box mb={3}>
          {selectedRow && (
            <>
              <Box
                display="flex"
                justifyContent="space-between"
                sx={{ backgroundColor: "#FCFCFD" }}
              >
                <Box>
                  <Typography variant="h5" fontWeight={700} mt={1}>
                    {selectedRow.requestId}
                  </Typography>
                  {selectedRow.requestedBy && selectedRow.requestedBy !== "—" && (
                    <Typography color="text.secondary" mt={0.5} fontSize={13}>
                      Requested By: {selectedRow.requestedBy}
                    </Typography>
                  )}
                </Box>

                <Box textAlign="right" display="flex" alignItems="center" gap={0.5}>
                  <StatusChip status={selectedRow.status} />
                </Box>
              </Box>

              <Divider />

              {loadingDetail ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                  <CircularProgress size={32} />
                </Box>
              ) : jobDetail ? (
                <div style={{ marginTop: 10 }}>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ mb: 2, color: "#1F2937" }}
                  >
                    Job Parameters
                  </Typography>

                  <Grid container spacing={3} alignItems="stretch">
                    {[
                      { label: "Plants", values: jobDetail.plants },
                      { label: "MRP Areas", values: jobDetail.mrpAreas },
                      { label: "Materials", values: jobDetail.materials },
                      { label: "MRP Controllers", values: jobDetail.mrpControllers },
                      { label: "MRP Types", values: jobDetail.mrpTypes },
                      { label: "MRP Groups", values: jobDetail.mrpGroups },
                      { label: "Purchasing Groups", values: jobDetail.purchasingGroups },
                      { label: "Vendor", values: jobDetail.vendor },
                      {
                        label: "Plant Material Status",
                        values: jobDetail.plantSpecificMaterialStatus,
                      },
                      {
                        label: "External Material Groups",
                        values: jobDetail.externalMaterialGroups,
                      },
                      { label: "Supplier PIN", values: jobDetail.supplierPin },
                      {
                        label: "From Date",
                        values: jobDetail.fromDate ? [jobDetail.fromDate] : [],
                      },
                      {
                        label: "To Date",
                        values: jobDetail.toDate ? [jobDetail.toDate] : [],
                      },
                      {
                        label: "User Email",
                        values: jobDetail.userEmail ? [jobDetail.userEmail] : [],
                      },
                      {
                        label: "Description",
                        values: jobDetail.description ? [jobDetail.description] : [],
                      },
                    ]
                      .filter((item) => item.values?.length)
                      .map((item) => (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={4}
                          key={item.label}
                          sx={{
                            display: "flex",
                          }}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              minHeight: 50,
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 13,
                                // fontWeight: 600,
                                color: "#878a92",
                                // textTransform: "uppercase",
                                letterSpacing: 1,
                                mb: 0.5,
                              }}
                            >
                              {item.label}
                            </Typography>

                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                                alignContent: "flex-start",
                                flex: 1,
                                border: "1px solid #E5E7EB",
                                padding: 0.5,
                              }}
                            >
                              {item.values.map((value, index) => (
                                <Chip
                                  key={index}
                                  label={value}
                                  size="small"
                                  sx={{
                                    bgcolor: "#EEF4FF",
                                    color: "#1D4ED8",
                                    fontWeight: 600,
                                    height: 28,
                                    borderRadius: "14px",
                                    "& .MuiChip-label": {
                                      px: 1.2,
                                    },
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                  </Grid>
                </div>
              ) : null}
            </>
          )}
        </Box>

      </div>
    );
  };

  return (
    <Box sx={{ px: 0.5, pb: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography fontSize={14} color="text.secondary">
          Monitor and track SAP background job executions from a single view.
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 3,
          border: "1px solid #dfe4ea",
          backgroundColor: "#ffffff",
        }}
      >
        <Box
          sx={{
            mb: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography fontSize={15} fontWeight={700} color="#111827">
            Scheduled Job Requests
          </Typography>

          {/* <TextField
            size="small"
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 240 }}
          /> */}
        </Box>

        {loadingJobs ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={300}>
            <CircularProgress size={36} />
          </Box>
        ) : (
          <Box
            className="ag-theme-quartz"
            sx={{
              height: 520,
              width: "100%",
              "& .ag-header": {
                backgroundColor: "#F8FAFC",
                borderBottom: "1px solid #EAECF0",
              },
              "& .ag-header-cell-text": {
                fontSize: 12,
                fontWeight: 700,
                color: "#344054",
                letterSpacing: 0.3,
              },
              "& .ag-row": {
                borderBottom: "1px solid #F2F4F7",
              },
              "& .ag-cell": {
                display: "flex",
                alignItems: "center",
                color: "#101828",
                fontSize: 13,
              },
            }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowHeight={64}
              animateRows
              onGridReady={onGridReady}
              pagination
              paginationPageSize={10}
              paginationPageSizeSelector={[10, 25, 50, 100]}
            />
          </Box>
        )}
      </Paper>

      <Modal
        className="showVariantModal_cls"
        open={openJobDetailsModal}
        handleClose={handleCloseJobDetailsModal}
        title="Job Details"
        fullWidth={true}
        action={
          <ShowJobDetailsFooter
            onClickCancel={handleCloseJobDetailsModal}
          />
        }
        content={
          <ShowJobDetailsContent
            jobDetail={jobDetail}
          />
        }
      />

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box width={460}>
          {selectedRow && (
            <>
              <Box
                p={3}
                display="flex"
                justifyContent="space-between"
                sx={{ backgroundColor: "#FCFCFD" }}
              >
                <Box>
                  <Typography variant="subtitle2" color="#667085">
                    JOB DETAILS
                  </Typography>
                  <Typography variant="h5" fontWeight={700} mt={1}>
                    {selectedRow.requestId}
                  </Typography>
                  {selectedRow.requestedBy && selectedRow.requestedBy !== "—" && (
                    <Typography color="text.secondary" mt={0.5} fontSize={13}>
                      {selectedRow.requestedBy}
                    </Typography>
                  )}
                </Box>

                <Box textAlign="right" display="flex" alignItems="flex-start" gap={0.5}>
                  <StatusChip status={selectedRow.status} />
                  <IconButton onClick={() => setDrawerOpen(false)}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>

              <Divider />

              {loadingDetail ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                  <CircularProgress size={32} />
                </Box>
              ) : jobDetail ? (
                <Box p={3} sx={{ overflowY: "auto", maxHeight: "calc(100vh - 140px)" }}>
                  <Typography variant="subtitle1" fontWeight={700} mb={3}>
                    JOB PARAMETERS
                  </Typography>

                  <Stack spacing={3}>
                    {jobDetail.plants?.length > 0 && (
                      <Parameter label="Plants" values={jobDetail.plants} />
                    )}
                    {jobDetail.mrpAreas?.length > 0 && (
                      <Parameter label="MRP Areas" values={jobDetail.mrpAreas} />
                    )}
                    {jobDetail.materials?.length > 0 && (
                      <Parameter label="Materials" values={jobDetail.materials} />
                    )}
                    {jobDetail.mrpControllers?.length > 0 && (
                      <Parameter label="MRP Controllers" values={jobDetail.mrpControllers} />
                    )}
                    {jobDetail.mrpTypes?.length > 0 && (
                      <Parameter label="MRP Types" values={jobDetail.mrpTypes} />
                    )}
                    {jobDetail.mrpGroups?.length > 0 && (
                      <Parameter label="MRP Groups" values={jobDetail.mrpGroups} />
                    )}
                    {jobDetail.purchasingGroups?.length > 0 && (
                      <Parameter label="Purchasing Groups" values={jobDetail.purchasingGroups} />
                    )}
                    {jobDetail.vendor?.length > 0 && (
                      <Parameter label="Vendor" values={jobDetail.vendor} />
                    )}
                    {jobDetail.plantSpecificMaterialStatus?.length > 0 && (
                      <Parameter label="Plant Material Status" values={jobDetail.plantSpecificMaterialStatus} />
                    )}
                    {jobDetail.externalMaterialGroups?.length > 0 && (
                      <Parameter label="External Material Groups" values={jobDetail.externalMaterialGroups} />
                    )}
                    {jobDetail.supplierPin?.length > 0 && (
                      <Parameter label="Supplier PIN" values={jobDetail.supplierPin} />
                    )}
                    {jobDetail.fromDate && (
                      <Parameter label="From Date" values={[jobDetail.fromDate]} />
                    )}
                    {jobDetail.toDate && (
                      <Parameter label="To Date" values={[jobDetail.toDate]} />
                    )}
                    {jobDetail.userEmail && (
                      <Parameter label="User Email" values={[jobDetail.userEmail]} />
                    )}
                    {jobDetail.description && (
                      <Parameter label="Description" values={[jobDetail.description]} />
                    )}
                  </Stack>
                </Box>
              ) : null}
            </>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}

function Parameter({ label, values = [] }) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={1}>
        {label}
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={0.75}>
        {values.map((v, i) => (
          <Chip
            key={i}
            label={v}
            size="small"
            sx={{
              background: "#e8f0fe",
              color: "#0d47a1",
              fontWeight: 600,
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
